import React from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Toggle from '../Toggle';
import { DisplaySettings } from './types';
import styles from './SettingsDrawer.module.css';

export interface SettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  displaySettings: DisplaySettings;
  onSettingsChange: (settings: DisplaySettings) => void;
}

export function SettingsDrawer({ isOpen, onClose, displaySettings, onSettingsChange }: SettingsDrawerProps) {
  const handleToggle = (section: keyof DisplaySettings, key: string, value: boolean) => {
    onSettingsChange({
      ...displaySettings,
      [section]: {
        ...displaySettings[section],
        [key]: value,
      },
    });
  };

  return createPortal(
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className={styles.drawerBackdrop}
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div className={`${styles.settingsDrawer} ${isOpen ? styles.settingsDrawerOpen : ''}`}>
        {/* Header */}
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>Display Settings</h2>
          <button
            onClick={onClose}
            className={styles.drawerCloseButton}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className={styles.drawerContent}>
          {/* Status Types Section */}
          <div className={styles.settingsSection}>
            <h3 className={styles.settingsSectionTitle}>Status Types</h3>
            <div className={styles.settingsSectionContent}>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Not Started</span>
                <Toggle
                  checked={displaySettings.statusTypes.notStarted}
                  onChange={(checked: boolean) => handleToggle('statusTypes', 'notStarted', checked)}
                  size="sm"
                />
              </label>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Between Moves</span>
                <Toggle
                  checked={displaySettings.statusTypes.betweenMoves}
                  onChange={(checked: boolean) => handleToggle('statusTypes', 'betweenMoves', checked)}
                  size="sm"
                />
              </label>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Current Move</span>
                <Toggle
                  checked={displaySettings.statusTypes.currentMove}
                  onChange={(checked: boolean) => handleToggle('statusTypes', 'currentMove', checked)}
                  size="sm"
                />
              </label>
            </div>
          </div>

          {/* Card Options Section */}
          <div className={styles.settingsSection}>
            <h3 className={styles.settingsSectionTitle}>Card Options</h3>
            <div className={styles.settingsSectionContent}>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Compact View</span>
                <Toggle
                  checked={displaySettings.cardOptions?.compactView ?? false}
                  onChange={(checked: boolean) => {
                    onSettingsChange({
                      ...displaySettings,
                      cardOptions: {
                        ...displaySettings.cardOptions,
                        compactView: checked,
                        superCompactView: checked ? (displaySettings.cardOptions?.superCompactView ?? false) : false,
                      },
                    });
                  }}
                  size="sm"
                />
              </label>
              {/* Super Compact sub-toggle — indented, only enabled when compactView is on */}
              <label
                className={`${styles.settingsToggleRow} pl-6 ${!displaySettings.cardOptions?.compactView ? 'opacity-40 pointer-events-none' : ''}`}
              >
                <span className={styles.settingsToggleLabel}>Super Compact</span>
                <Toggle
                  checked={displaySettings.cardOptions?.superCompactView ?? false}
                  onChange={(checked: boolean) => handleToggle('cardOptions', 'superCompactView', checked)}
                  size="sm"
                  disabled={!displaySettings.cardOptions?.compactView}
                />
              </label>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Show Container #</span>
                <Toggle
                  checked={displaySettings.cardOptions?.showContainerNumber ?? false}
                  onChange={(checked: boolean) => handleToggle('cardOptions', 'showContainerNumber', checked)}
                  size="sm"
                />
              </label>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Show Size</span>
                <Toggle
                  checked={displaySettings.cardOptions?.showSize ?? false}
                  onChange={(checked: boolean) => handleToggle('cardOptions', 'showSize', checked)}
                  size="sm"
                />
              </label>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Show Chassis</span>
                <Toggle
                  checked={displaySettings.cardOptions?.showChassis ?? false}
                  onChange={(checked: boolean) => handleToggle('cardOptions', 'showChassis', checked)}
                  size="sm"
                />
              </label>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Show Account Status</span>
                <Toggle
                  checked={displaySettings.cardOptions?.showAccountStatus ?? true}
                  onChange={(checked: boolean) => handleToggle('cardOptions', 'showAccountStatus', checked)}
                  size="sm"
                />
              </label>
            </div>
          </div>

          {/* Other Section */}
          <div className={styles.settingsSection}>
            <h3 className={styles.settingsSectionTitle}>Other</h3>
            <div className={styles.settingsSectionContent}>
              <label className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Endorsements</span>
                <Toggle
                  checked={displaySettings.other.endorsements}
                  onChange={(checked: boolean) => handleToggle('other', 'endorsements', checked)}
                  size="sm"
                />
              </label>
              <div className={styles.settingsToggleRow}>
                <span className={styles.settingsToggleLabel}>Ghost Cards per Position</span>
                <select
                  value={displaySettings.other.ghostCardCount || 1}
                  onChange={(e) => onSettingsChange({
                    ...displaySettings,
                    other: {
                      ...displaySettings.other,
                      ghostCardCount: parseInt(e.target.value, 10),
                    },
                  })}
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-body"
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export default SettingsDrawer;
